/**
 * Time budget for multipart file transfers (#5665).
 *
 * The standard `$http` client caps every request at 30 seconds, which is right
 * for a JSON call and wrong for a file transfer: any document needing longer
 * was aborted by the browser, and the user was told "the server took too long
 * to respond" — a client-side abort wearing a server error's words.
 *
 * The budget is a fixed base (connection setup, the server's write to GCS, the
 * JSON response) plus an allowance derived from the payload, assuming a floor
 * throughput slow enough to cover a poor clinic connection. It is clamped at
 * the top so a genuinely stalled transfer still fails rather than hanging.
 *
 * Note this is axios' total-request timeout, not an inactivity timeout — the
 * clock runs from the first byte regardless of whether the transfer is
 * progressing, which is why the allowance is generous.
 */

// Largest single file the upload dialog accepts. The field advertises this, and
// the API accepts a 70 MB POST body (api/frankenphp/conf.d/app.prod.ini).
export const MAX_UPLOAD_FILE_BYTES = 50 * 1024 * 1024;
// Largest combined selection. Every selected file goes up in one multipart
// request, so the total has to clear the API body limit as well as the per-file
// cap — two files inside the per-file limit can still exceed it together.
export const MAX_UPLOAD_TOTAL_BYTES = 60 * 1024 * 1024;

// Allowance independent of payload size.
export const UPLOAD_TIMEOUT_BASE_MS = 60000;
// Assumed worst-case sustained throughput, in bytes per millisecond (~50 KB/s,
// i.e. a 400 kbps link). Anything slower than this is treated as stalled.
export const UPLOAD_FLOOR_BYTES_PER_MS = 50;
// Upper bound, derived from the largest selection the dialog permits rather than
// hand-tuned: a literal here silently goes stale the moment a cap is raised, and
// the symptom is the bug this module exists to fix — a transfer that was still
// progressing aborted with "the upload took too long". Because it is derived, it
// can never cut a valid selection short; it stays a real backstop only for the
// callers that upload without validating size first.
export const UPLOAD_TIMEOUT_MAX_MS = Math.round(UPLOAD_TIMEOUT_BASE_MS + MAX_UPLOAD_TOTAL_BYTES / UPLOAD_FLOOR_BYTES_PER_MS);

/**
 * Total bytes across a file list. Accepts the array `v-file-input` produces and
 * the keyed object the image uploader passes.
 *
 * @param {File[]|Object.<string, File>} files
 * @returns {number} total size in bytes, 0 when unmeasurable
 */
export function totalUploadBytes(files) {
    if (!files) return 0;

    const list = files instanceof File ? [files] : Object.values(files);

    return list.reduce((sum, file) => sum + (Number(file?.size) || 0), 0);
}

/**
 * Per-request axios timeout for a multipart upload of `files`.
 *
 * Returns 0 (axios' "no timeout") when VITE_NO_TIMEOUT is set, matching how the
 * shared clients disable their own caps in development.
 *
 * @param {File[]|Object.<string, File>} files
 * @returns {number} timeout in milliseconds
 */
export function uploadTimeout(files) {
    if (import.meta.env.VITE_NO_TIMEOUT) return 0;

    const budget = UPLOAD_TIMEOUT_BASE_MS + totalUploadBytes(files) / UPLOAD_FLOOR_BYTES_PER_MS;

    return Math.min(Math.round(budget), UPLOAD_TIMEOUT_MAX_MS);
}

/**
 * Axios request config shared by every multipart upload: the size-derived
 * timeout, the multipart content type, a progress callback when one is given,
 * and the `isFileUpload` marker the error handler reads to word a timeout
 * message for a transfer rather than for a slow server.
 *
 * @param {File[]|Object.<string, File>} files
 * @param {(percent: number) => void} [onProgress] receives 0-100, or -1 when
 *   the total length is unknown and progress cannot be computed
 * @returns {object} axios request config
 */
export function uploadRequestConfig(files, onProgress) {
    const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: uploadTimeout(files),
        isFileUpload: true,
    };

    if (typeof onProgress === 'function') {
        config.onUploadProgress = (event) => {
            const total = event.total || totalUploadBytes(files);
            if (!total) {
                onProgress(-1);
                return;
            }
            onProgress(Math.min(100, Math.round((event.loaded / total) * 100)));
        };
    }

    return config;
}
